import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { isBefore } from 'date-fns';
import isEqual from 'lodash.isequal';

// Utils
import { getSubscriptionCaption } from 'utils/detalization';

// Components
import { MODAL_NAMES } from 'constants/modal';
import {
  DETALIZATION_MODAL_DOWNLOAD_OPTIONS,
  DETALIZATION_MODAL_COMMON,
} from 'constants/detalization';
import { COMMON_ERRORS } from 'constants/common';
import { openPopup } from 'actions';
import { Text } from '@beef/ui-kit';
import {
  NumbersListSection,
  EmailSection,
  SelectDateSection,
  ControlsSection,
  SelectReportType,
} from './components';
import {
  saveRegularDetalizationReportData,
  setSaveRegularDetalizationReportInitialControlsData,
} from '../../../../store/regularDetalizationReport/actions';
import { setDetalizationConfirmData } from '../../../../store/personalDataStore/actions';

// Style
import styles from './styles.pcss';

const cx = classNames.bind(styles);

export const ContentSection = ({ isMobile }) => {
  const dispatch = useDispatch();

  const [selectedSubscriptionDay, setSelectedSubscriptionDay] = useState('');
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [initialActiveNumbers, setInitialActiveNumbers] = useState([]);
  const [selectedDownloadTab, setSelectedDownloadTab] = useState('');

  const {
    telNumbers,
    subscriptionStatus,
    startSubscriptionDate,
    dueSubscriptionDate,
    reportType,
    isUpdatingError,
    isUpdating,
    email,
    isEmailExist,
    isEmailConfirmed,
  } = useSelector(({ detalizationNew }) => ({
    telNumbers: detalizationNew.regularDetalizationReport.numbers,
    subscriptionStatus: detalizationNew.regularDetalizationReport.subscriptionStatus,
    startSubscriptionDate: detalizationNew.regularDetalizationReport.startSubscriptionDate,
    dueSubscriptionDate: detalizationNew.regularDetalizationReport.dueSubscriptionDate,
    reportType: detalizationNew.regularDetalizationReport.reportType,
    isUpdatingError: detalizationNew.regularDetalizationReport.isUpdatingError,
    isUpdating: detalizationNew.regularDetalizationReport.isUpdating,
    email: detalizationNew.personalData.emailAddress,
    isEmailExist: detalizationNew.personalData.isExist,
    isEmailConfirmed: detalizationNew.personalData.isEmailConfirmed,
    isDataConfirmingSuccess: detalizationNew.personalData.isDataConfirmingSuccess,
    isUpdatingSuccess: detalizationNew.regularDetalizationReport.isUpdatingSuccess,
  }), shallowEqual);

  const isSubscriptionStatusActive = subscriptionStatus === 'active';

  const onOpenEmailModal = useCallback(() => {
    dispatch(setSaveRegularDetalizationReportInitialControlsData());
    dispatch(setDetalizationConfirmData());
    dispatch(openPopup(MODAL_NAMES.EMAIL));
  }, [dispatch]);

  const onSaveRegularDetalizationReport = useCallback(() => {
    let selectedFormat = selectedDownloadTab;

    if (selectedFormat === DETALIZATION_MODAL_DOWNLOAD_OPTIONS.xls03) {
      selectedFormat = 'xls';
    }

    if (selectedFormat === DETALIZATION_MODAL_DOWNLOAD_OPTIONS.xls) {
      selectedFormat = 'xlsx';
    }

    dispatch(saveRegularDetalizationReportData({
      reportType: selectedFormat.toLowerCase(),
      activeNumbers: selectedNumbers,
      startDate: selectedSubscriptionDay.toString(),
    }));
  }, [
    dispatch,
    selectedDownloadTab,
    selectedSubscriptionDay,
    selectedNumbers,
  ]);

  const onSetSelectedNumbers = useCallback((tel) => () => {
    let numbers = [...selectedNumbers];

    if (numbers.includes(tel)) {
      numbers = numbers.filter((number) => number !== tel);
    } else {
      numbers.push(tel);
    }

    setSelectedNumbers(numbers);
  }, [selectedNumbers]);

  useEffect(() => {
    if (telNumbers.length > 0) {
      const activeNumbers = telNumbers.filter((tel) => tel.active).map((tel) => tel.ctn);

      setInitialActiveNumbers(activeNumbers);
      setSelectedNumbers(activeNumbers);
    }

    setSelectedSubscriptionDay(startSubscriptionDate);
    setSelectedDownloadTab(reportType);
  }, [
    telNumbers,
    subscriptionStatus,
    reportType,
    startSubscriptionDate,
  ]);

  const isDisabledSaveButton = useMemo(() => {
    const isEqualDays = selectedSubscriptionDay === startSubscriptionDate.toString();
    const isEqualReportType = reportType === selectedDownloadTab;

    if (subscriptionStatus === 'disable') {
      return selectedNumbers.length === 0 || !isEmailExist || !isEmailConfirmed;
    } if (subscriptionStatus === 'active') {
      return !(isEmailExist && (!isEqual(selectedNumbers, initialActiveNumbers) || !isEqualDays || !isEqualReportType));
    }
  }, [
    selectedSubscriptionDay,
    startSubscriptionDate,
    selectedDownloadTab,
    reportType,
    selectedNumbers,
    initialActiveNumbers,
    isEmailExist,
  ]);

  const isExpiredSubscriptionDate = isBefore(new Date(dueSubscriptionDate), new Date());

  return (
    <div className={cx('wrapper')}>
      <Text size="size3-b">{DETALIZATION_MODAL_COMMON.REGULAR_DETALIZATION_REPORT_CAPTION}</Text>
      {isSubscriptionStatusActive && !isExpiredSubscriptionDate && (
        <div className={cx('subscription')}>
          <Text size="size5-r-l">{getSubscriptionCaption(dueSubscriptionDate)}</Text>
        </div>
      )}
      <NumbersListSection
        options={telNumbers}
        value={selectedNumbers}
        onClick={onSetSelectedNumbers}
      />
      <EmailSection
        isConfirmed={isEmailConfirmed}
        onClick={onOpenEmailModal}
        email={email}
      />
      <SelectDateSection
        onChange={setSelectedSubscriptionDay}
        value={selectedSubscriptionDay}
        isMobile={isMobile}
      />
      <SelectReportType
        onChange={setSelectedDownloadTab}
        isError={isUpdatingError}
        value={selectedDownloadTab}
      />
      {isUpdatingError && (
        <div className={cx('error')}>
          <Text size="size5-r-l">{COMMON_ERRORS.WENT_WRONG}</Text>
        </div>
      )}
      <ControlsSection
        saveButtonDisabled={isDisabledSaveButton}
        isSaveLoading={isUpdating}
        onSave={onSaveRegularDetalizationReport}
      />
    </div>
  );
};

ContentSection.propTypes = {
  isMobile: PropTypes.bool,
};
